// A thread with an agent on a person's machine, and the box they ask it in
// (RFC-060's builder, RFC-063's author drafting a kit): what was asked and
// answered, its tool calls in words, what it asks permission for, and the
// model not signed in or the agent stuck, said as such.
import { useState } from "react";
import { notSignedIn, saidText, stoppedStuck, toolWords } from "../builder";
import { Markdown } from "../markdown";
import type { Thread } from "../workbench";
import { t } from "../i18n";

/** What the model not signed in looks like on the page: said, with where to sign it in (byj-1wv.115.27). */
export function SignInNeeded({ onSignIn }: { onSignIn?: () => void }) {
  return (
    <div className="row wrap" data-testid="sign-in-needed">
      <span className="grow">{t("agents:notSignedIn")}</span>
      {onSignIn && (
        <button className="small" onClick={onSignIn}>
          {t("agents:signInThere")}
        </button>
      )}
    </div>
  );
}

/** An agent that stopped before answering, said in words, byj's own message kept under a fold (byj-1wv.115.32). */
function Stuck({ text }: { text: string }) {
  return (
    <div className="stack" data-testid="stuck">
      <span>{t("agents:stuck")}</span>
      <details className="muted small">
        <summary>{t("agents:stuckDetail")}</summary>
        <pre className="builder-instruction">{text}</pre>
      </details>
    </div>
  );
}

/** A tool call as a step in words; the same step done again folded into one, counted (byj-1wv.115.26). */
function stepText(title: string, status: string, times: number): string {
  const { key, arg } = toolWords(title);
  const what = key ? t(`agents:did.${key as "read"}`, { arg }) : title;
  return `${what}${times > 1 ? ` ×${times}` : ""} — ${t(`agents:tool.${status as "completed"}`)}`;
}

/** A thread with an agent: what was asked and answered, its tool calls, and what it asks permission for. */
export function ThreadView({ thread, onChoose, onSignIn }: { thread: Thread; onChoose: (p: Extract<Thread["items"][number], { kind: "permission" }>, optionId: string) => void; onSignIn?: () => void }) {
  if (thread.items.length === 0) return null;
  // a step repeated in a row is one line, counted: five examples written, not five lines
  const fold = (x: Thread["items"][number] | undefined) => {
    if (x?.kind !== "tool") return null;
    const { key, arg } = toolWords(x.title);
    return arg ? null : `${key || x.title}|${x.status}`;
  };
  const times = thread.items.map((x, i) => {
    const k = fold(x);
    if (k === null) return 1;
    if (fold(thread.items[i - 1]) === k) return 0;
    let n = 1;
    while (fold(thread.items[i + n]) === k) n++;
    return n;
  });
  return (
    <ol className="stack builder-thread" data-testid="builder-thread">
      {thread.items.map((x, i) => {
        switch (x.kind) {
          case "user":
            return (
              <li key={i} className="builder-said">
                <strong>{t("agents:you")}</strong> {saidText(x.text)}
              </li>
            );
          case "agent":
            if (notSignedIn(x.text)) return <li key={i}><SignInNeeded onSignIn={onSignIn} /></li>;
            if (stoppedStuck(x.text)) return <li key={i}><Stuck text={x.text} /></li>;
            return (
              <li key={i} className="builder-answer">
                <Markdown text={x.text} />
              </li>
            );
          case "tool":
            if (times[i] === 0) return null;
            return (
              <li key={i} className="muted small">
                {stepText(x.title, x.status, times[i]!)}
              </li>
            );
          case "permission":
            return (
              <li key={i} className="row wrap">
                <span className="grow">{x.title}</span>
                {x.chosen
                  ? <span className="muted small">{x.options.find((o) => o.optionId === x.chosen)?.name ?? x.chosen}</span>
                  : x.options.map((o) => (
                      <button key={o.optionId} className="small" data-kind={o.kind} onClick={() => onChoose(x, o.optionId)}>
                        {o.name ?? o.optionId}
                      </button>
                    ))}
              </li>
            );
          case "error":
            if (notSignedIn(x.text)) return <li key={i}><SignInNeeded onSignIn={onSignIn} /></li>;
            if (stoppedStuck(x.text)) return <li key={i}><Stuck text={x.text} /></li>;
            return (
              <li key={i} className="error">
                {x.text}
              </li>
            );
          default:
            return null;
        }
      })}
      {thread.busy && <li className="muted small">{t("agents:working")}</li>}
    </ol>
  );
}

/** A prompt to an agent and its answer, in a step. */
export function Ask({ placeholder, label, onAsk, busy, disabled, rows = 4 }: { placeholder: string; label: string; onAsk: (text: string) => boolean; busy: boolean; disabled: boolean; rows?: number }) {
  const [text, setText] = useState("");
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim() && onAsk(text.trim())) setText("");
      }}
    >
      <textarea rows={rows} value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} disabled={disabled} />
      <div className="row">
        <span className="grow" />
        <button className="primary" type="submit" disabled={disabled || busy || !text.trim()}>
          {label}
        </button>
      </div>
    </form>
  );
}
